import React, {  useMemo, useRef, useState } from "react"
import { FiChevronLeft, FiChevronRight } from "react-icons/fi"
import { useLang } from "hooks/useLang"
import { IFinalEvent, ShortsVideo } from "types/event"
import NewEventCard from "components/NewEventCard"
import Button from "components/Form/Button"
import RenderStory from "components/video/renderStories"
import useWindowSize from "hooks/useWindowSize"
import ScrollCarousel from "components/Carousel/ScrollCarousel"

interface ISection {
    title?: string
    description?: string
    children?: React.ReactNode
    id?: string
    events?: IFinalEvent[]
    className?: string
    maxEventsInARow?: 4 | 5
    layout?: "grid" | "slider"
    videos?: ShortsVideo[]
    aspectRatio?: "widescreen" | "portrait"
    initialIsMobile?: boolean
}

const SectionV2 = ({
    title,
    description,
    children,
    id,
    events = [],
    maxEventsInARow = 4,
    layout = "grid",
    aspectRatio = "widescreen",
    className: customClassName,
    videos = [],
    initialIsMobile = false,
}: ISection) => {
    const { isAr } = useLang()

    const [visibleCount, setVisibleCount] = useState<number>(10)

    const gridClass = `
    grid 
    grid-cols-1 
    gap-x-[20px]
    gap-y-8
    sm:grid-cols-2 
    md:grid-cols-${maxEventsInARow === 5 ? 3 : 2} 
    lg:grid-cols-${maxEventsInARow === 5 ? 4 : 3} 
    xl:grid-cols-${maxEventsInARow} 
    2xl:grid-cols-${maxEventsInARow}
  `

    const scrollRef = useRef<HTMLDivElement>(null)
    const { width, isMobile } = useWindowSize()
    
    // Keep first paint sizing aligned with SSR device hint to reduce card width jumps
    const effectiveWidth = width || (initialIsMobile ? 390 : 1280)

    const sliderConfig = useMemo(() => {
        const hasVideos = videos.length > 0
        if (effectiveWidth < 400) {
            return { perView: hasVideos ? 2.5 : 1.25, spacing: 20 }
        }
        if (effectiveWidth < 640) {
            return { perView: hasVideos ? 4 : 1.25, spacing: 20 }
        }
        if (effectiveWidth < 768) {
            return { perView: hasVideos ? 4 : 1, spacing: 20 }
        }
        if (effectiveWidth < 1024) {
            return { perView: hasVideos ? 4 : 2, spacing: 40 }
        }
        if (effectiveWidth < 1280) {
            return { perView: maxEventsInARow === 5 ? 4 : 3, spacing: 40 }
        }
        if (effectiveWidth < 1536) {
            return { perView: maxEventsInARow ?? 4, spacing: 40 }
        }
        return { perView: hasVideos ? 6 : (maxEventsInARow ?? 4), spacing: 25 }
    }, [maxEventsInARow, videos.length, effectiveWidth])

    const scrollByPage = (direction: "prev" | "next") => {
        if (!scrollRef.current) return
        const container = scrollRef.current
        const scrollAmount = container.clientWidth
        container.scrollBy({
            left: direction === "next" ? scrollAmount : -scrollAmount,
            behavior: "smooth",
        })
    }

    const visibleEvents =
        layout === "grid" ? events.slice(0, visibleCount) : events

    if (events.length === 0 && videos.length === 0 && !children) {
        return null
    }

    return (
        <section
            id={id}
            className={`my-10 flex flex-col gap-5  md:gap-8 overflow-hidden ${customClassName}`}
        >
            <div className="flex items-end justify-between layout lg:max-w-[1230px] ">
                <div className="flex flex-col gap-0 ">
                    <h2 className="text-xl font-bold text-black dark:text-white md:text-2xl capitalize">
                        {title}
                    </h2>
                    <p className="hidden max-w-[220px] text-sm text-gray-700 dark:text-gray-100 sm:max-w-[400px] md:block">
                        {description}
                    </p>
                </div>
                {!isMobile ? (
                    layout === "slider" && events?.length >= 5 ? (
                        <div className="flex items-center gap-2 md:gap-4">
                            <FiChevronLeft
                                className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-gray-100 p-2 transition-all dark:border-transparent dark:bg-primary-theme-100 md:h-10 md:w-10 md:hover:bg-gray-200 md:dark:hover:bg-opacity-70 ${
                                    isAr ? "rotate-180" : "rotate-0"
                                }`}
                                onClick={() => scrollByPage("prev")}
                            />
                            <FiChevronRight
                                className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-gray-100 p-2 transition-all dark:border-transparent dark:bg-primary-theme-100 md:h-10 md:w-10 md:hover:bg-gray-200 md:dark:hover:bg-opacity-70 ${
                                    isAr ? "rotate-180" : "rotate-0"
                                }`}
                                onClick={() => scrollByPage("next")}
                            />
                        </div>
                    ) : null
                ) : null}
            </div>

            {layout === "slider" ? (
                <div className="relative mx-auto w-full max-w-8xl lg:max-w-[1230px] px-5 md:px-10 xl:px-6">
                    <div className="overflow-hidden">
                        <ScrollCarousel
                            scrollRef={scrollRef}
                            perView={sliderConfig.perView}
                            spacing={sliderConfig.spacing}
                        >
                            {events && events.length > 0
                                ? events.map((event, index) => (
                                      <NewEventCard
                                          key={event._id}
                                          event={event}
                                          aspectRatio={aspectRatio}
                                          priorityImage={index < 4}
                                      />
                                  ))
                                : React.Children.map(children, (child, index) => (
                                      <div key={index}>{child}</div>
                                  ))}

                            {videos && videos.length > 0 && (
                                <RenderStory videos={videos} />
                            )}
                        </ScrollCarousel>
                    </div>
                </div>
            ) : (
                <>
                    <div className={`layout ${gridClass} lg:max-w-[1230px]`}>
                        {visibleEvents && visibleEvents.length > 0
                            ? visibleEvents.map((event, index) => (
                                  <NewEventCard
                                      key={event._id}
                                      event={event}
                                      aspectRatio={aspectRatio}
                                      priorityImage={index < 4}
                                  />
                              ))
                            : children}
                    </div>
                    {events.length > visibleCount && (
                        <Button
                            className="mx-auto w-fit cursor-pointer rounded-md border border-transparent bg-gray-100 px-4 py-2 text-center text-xs transition-all dark:border-transparent dark:bg-primary-theme-100 md:px-6 md:text-sm  md:dark:hover:bg-opacity-70"
                            onClick={() => setVisibleCount((prev) => prev + 10)}
                        >
                            See more
                        </Button>
                    )}
                </>
            )}
        </section>
    )
}

export default SectionV2
